import { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { AlertCircle, CheckCircle2, Clock, FileText } from 'lucide-react';

interface InvestigationChecklist {
  id: string;
  label: string;
  completed: boolean;
  notes?: string;
  required: boolean;
}

interface SafetyInvestigationFormProps {
  reportId: string;
  issueType: string;
  severity: string;
  description: string;
  conversationId: string | null;
  onComplete: (investigation: any) => void;
  onCancel: () => void;
}

const getChecklistByIssueType = (issueType: string): InvestigationChecklist[] => {
  const baseChecklist: InvestigationChecklist[] = [
    { id: 'read_report', label: 'Read and understand the full report', completed: false, required: true },
    { id: 'verify_user', label: 'Verify user identity and account status', completed: false, required: true },
  ];

  const specificChecklists: Record<string, InvestigationChecklist[]> = {
    harmful_ai_advice: [
      { id: 'review_conversation', label: 'Review full conversation transcript', completed: false, required: true },
      { id: 'identify_harmful_content', label: 'Identify specific harmful statements made by AI', completed: false, required: true },
      { id: 'check_prompt_logic', label: 'Check if safety prompts/crisis detection triggered correctly', completed: false, required: true },
      { id: 'test_reproduction', label: 'Test if issue can be reproduced with similar inputs', completed: false, required: true },
      { id: 'update_prompts', label: 'Update AI prompts/guardrails to prevent recurrence', completed: false, required: true },
      { id: 'clinical_review', label: 'Clinical lead review for therapeutic appropriateness', completed: false, required: true },
    ],
    crisis_mishandling: [
      { id: 'review_conversation', label: 'Review conversation where crisis occurred', completed: false, required: true },
      { id: 'check_detection', label: 'Verify crisis detection triggered (check risk_flags table)', completed: false, required: true },
      { id: 'check_resources', label: 'Verify emergency resources were displayed', completed: false, required: true },
      { id: 'check_crisis_mode', label: 'Verify Crisis Mode activated correctly', completed: false, required: true },
      { id: 'test_triggers', label: 'Test crisis triggers with reported phrases', completed: false, required: true },
      { id: 'user_contacted_help', label: 'Verify if user contacted emergency services', completed: false, required: false },
      { id: 'update_detection', label: 'Update crisis detection logic if gaps found', completed: false, required: true },
    ],
    eating_disorder_enabling: [
      { id: 'review_conversation', label: 'Review full conversation for triggering content', completed: false, required: true },
      { id: 'identify_triggers', label: 'Document specific triggering language or advice', completed: false, required: true },
      { id: 'check_ed_prompts', label: 'Review eating disorder safety prompts', completed: false, required: true },
      { id: 'clinical_assessment', label: 'Clinical lead assessment of harm potential', completed: false, required: true },
      { id: 'user_risk_level', label: 'Assess user\'s current risk level', completed: false, required: true },
      { id: 'update_safeguards', label: 'Update ED-specific safeguards', completed: false, required: true },
      { id: 'consider_restriction', label: 'Consider if account restrictions needed', completed: false, required: false },
    ],
    data_error: [
      { id: 'identify_error', label: 'Identify specific data error reported', completed: false, required: true },
      { id: 'check_database', label: 'Query database to verify error exists', completed: false, required: true },
      { id: 'assess_impact', label: 'Assess impact (how many users affected)', completed: false, required: true },
      { id: 'fix_data', label: 'Correct erroneous data in database', completed: false, required: true },
      { id: 'identify_root_cause', label: 'Identify root cause (code bug, migration issue, etc)', completed: false, required: true },
      { id: 'fix_code', label: 'Fix underlying code issue', completed: false, required: true },
      { id: 'test_fix', label: 'Test fix doesn\'t create new issues', completed: false, required: true },
      { id: 'notify_affected', label: 'Notify affected users if appropriate', completed: false, required: false },
    ],
    privacy_concern: [
      { id: 'identify_exposure', label: 'Identify what data was potentially exposed', completed: false, required: true },
      { id: 'verify_exposure', label: 'Verify if data was actually accessed by unauthorized parties', completed: false, required: true },
      { id: 'assess_sensitivity', label: 'Assess sensitivity of exposed data (PII, health data, etc)', completed: false, required: true },
      { id: 'check_rls', label: 'Review RLS policies on affected tables', completed: false, required: true },
      { id: 'fix_vulnerability', label: 'Fix security vulnerability immediately', completed: false, required: true },
      { id: 'test_fix', label: 'Test fix with penetration testing', completed: false, required: true },
      { id: 'ico_notification', label: 'Determine if ICO notification required (72hr for breach)', completed: false, required: true },
      { id: 'user_notification', label: 'Notify affected users of breach under GDPR Article 34', completed: false, required: true },
      { id: 'document_breach', label: 'Document breach in breach register', completed: false, required: true },
    ],
  };

  return [...baseChecklist, ...(specificChecklists[issueType] || [])];
};

export default function SafetyInvestigationForm({
  reportId,
  issueType,
  severity,
  description,
  conversationId,
  onComplete,
  onCancel,
}: SafetyInvestigationFormProps) {
  const [checklist, setChecklist] = useState<InvestigationChecklist[]>(
    getChecklistByIssueType(issueType)
  );
  const [investigatorName, setInvestigatorName] = useState('');
  const [timeSpent, setTimeSpent] = useState('');
  const [riskAssessment, setRiskAssessment] = useState<'no_risk' | 'low' | 'medium' | 'high' | 'critical'>('low');
  const [rootCause, setRootCause] = useState('');
  const [actionsTaken, setActionsTaken] = useState('');
  const [preventativeMeasures, setPreventativeMeasures] = useState('');
  const [followUpRequired, setFollowUpRequired] = useState(false);
  const [followUpDate, setFollowUpDate] = useState('');
  const [followUpPerson, setFollowUpPerson] = useState('');
  const [clinicalReviewRequired, setClinicalReviewRequired] = useState(false);
  const [clinicalReviewer, setClinicalReviewer] = useState('');
  const [legalEscalation, setLegalEscalation] = useState(false);
  const [legalNotes, setLegalNotes] = useState('');
  const [icoNotificationRequired, setIcoNotificationRequired] = useState(false);
  const [userNotificationRequired, setUserNotificationRequired] = useState(false);
  const [resolutionSummary, setResolutionSummary] = useState('');

  const updateChecklist = (id: string, completed: boolean, notes?: string) => {
    setChecklist(prev => prev.map(item => 
      item.id === id ? { ...item, completed, notes } : item
    ));
  };

  const requiredItems = checklist.filter(item => item.required);
  const completedRequired = requiredItems.filter(item => item.completed).length;
  const completionPercentage = Math.round((completedRequired / requiredItems.length) * 100);
  const canComplete = completionPercentage === 100 && investigatorName && rootCause && actionsTaken && resolutionSummary;

  const handleComplete = () => {
    const investigation = {
      report_id: reportId,
      investigator_name: investigatorName,
      time_spent_minutes: parseInt(timeSpent) || null,
      risk_assessment: riskAssessment,
      root_cause: rootCause,
      actions_taken: actionsTaken,
      preventative_measures: preventativeMeasures,
      follow_up_required: followUpRequired,
      follow_up_date: followUpDate || null,
      follow_up_person: followUpPerson || null,
      clinical_review_required: clinicalReviewRequired,
      clinical_reviewer: clinicalReviewer || null,
      legal_escalation: legalEscalation,
      legal_notes: legalNotes || null,
      ico_notification_required: icoNotificationRequired,
      user_notification_required: userNotificationRequired,
      resolution_summary: resolutionSummary,
      checklist_items: checklist,
      completed_at: new Date().toISOString(),
    };

    onComplete(investigation);
  };

  return (
    <div className="space-y-6">
      {/* Progress Indicator */}
      <Card className="p-4 bg-accent/50">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            {completionPercentage === 100 ? (
              <CheckCircle2 className="h-5 w-5 text-green-500" />
            ) : (
              <Clock className="h-5 w-5 text-orange-500" />
            )}
            <span className="font-semibold">Investigation Progress</span>
          </div>
          <Badge variant={completionPercentage === 100 ? 'default' : 'secondary'}>
            {completedRequired}/{requiredItems.length} Required Steps
          </Badge>
        </div>
        <div className="w-full bg-secondary rounded-full h-2">
          <div 
            className="bg-primary h-2 rounded-full transition-all duration-300"
            style={{ width: `${completionPercentage}%` }}
          />
        </div>
        <p className="text-xs text-muted-foreground mt-2">
          Complete all required steps to close this investigation
        </p>
      </Card>

      {/* Investigator Details */}
      <Card className="p-4">
        <h3 className="font-semibold mb-4 flex items-center gap-2">
          <FileText className="h-4 w-4" />
          Investigator Details
        </h3>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="investigator">Your Name *</Label>
            <Input
              id="investigator"
              value={investigatorName}
              onChange={(e) => setInvestigatorName(e.target.value)}
              placeholder="Amanda Charles"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="timeSpent">Time Spent (minutes)</Label>
            <Input
              id="timeSpent"
              type="number"
              value={timeSpent}
              onChange={(e) => setTimeSpent(e.target.value)}
              placeholder="45"
            />
          </div>
        </div>
      </Card>

      {/* Investigation Checklist */}
      <Card className="p-4">
        <h3 className="font-semibold mb-4">Investigation Checklist</h3>
        <p className="text-sm text-muted-foreground mb-4">
          Follow each step below. Required steps are marked with *
        </p>
        <div className="space-y-4">
          {checklist.map((item) => (
            <div key={item.id} className="space-y-2">
              <div className="flex items-start gap-3">
                <Checkbox
                  id={item.id}
                  checked={item.completed}
                  onCheckedChange={(checked) => updateChecklist(item.id, checked as boolean)}
                  className="mt-1"
                />
                <div className="flex-1">
                  <Label htmlFor={item.id} className="cursor-pointer font-normal">
                    {item.label}
                    {item.required && <span className="text-red-500 ml-1">*</span>}
                  </Label>
                  {item.completed && (
                    <Textarea
                      value={item.notes || ''}
                      onChange={(e) => updateChecklist(item.id, true, e.target.value)}
                      placeholder="Document findings for this step..."
                      className="mt-2 text-sm"
                      rows={2}
                    />
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Risk Assessment */}
      <Card className="p-4">
        <h3 className="font-semibold mb-4">Risk Assessment *</h3>
        <RadioGroup value={riskAssessment} onValueChange={(value: any) => setRiskAssessment(value)}>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="no_risk" id="no_risk" />
            <Label htmlFor="no_risk">No Risk - False alarm or resolved immediately</Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="low" id="low" />
            <Label htmlFor="low">Low Risk - Minor issue, no user harm</Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="medium" id="medium" />
            <Label htmlFor="medium">Medium Risk - Potential for harm, requires monitoring</Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="high" id="high" />
            <Label htmlFor="high">High Risk - User harm occurred or system vulnerability found</Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="critical" id="critical" />
            <Label htmlFor="critical">Critical Risk - Serious harm or widespread system failure</Label>
          </div>
        </RadioGroup>
      </Card>

      {/* Root Cause Analysis */}
      <Card className="p-4">
        <h3 className="font-semibold mb-4">Root Cause Analysis *</h3>
        <Textarea
          value={rootCause}
          onChange={(e) => setRootCause(e.target.value)}
          placeholder="What was the underlying cause of this issue? (e.g., AI prompt gap, crisis detection threshold too high, database RLS policy misconfiguration)"
          rows={4}
        />
      </Card>

      {/* Actions Taken */}
      <Card className="p-4">
        <h3 className="font-semibold mb-4">Actions Taken *</h3>
        <Textarea
          value={actionsTaken}
          onChange={(e) => setActionsTaken(e.target.value)}
          placeholder="What specific actions did you take to resolve this issue? (e.g., Updated crisis detection to include phrase 'X', fixed RLS policy on table Y, contacted user to apologise)"
          rows={4}
        />
      </Card>

      {/* Preventative Measures */}
      <Card className="p-4">
        <h3 className="font-semibold mb-4">Preventative Measures</h3>
        <Textarea
          value={preventativeMeasures}
          onChange={(e) => setPreventativeMeasures(e.target.value)}
          placeholder="What measures were implemented to prevent recurrence? (e.g., Added new test cases, updated documentation, scheduled monthly audit of crisis responses)"
          rows={4}
        />
      </Card>

      <Separator />

      {/* Escalation & Follow-up */}
      <Card className="p-4">
        <h3 className="font-semibold mb-4">Escalation & Follow-up</h3>
        
        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <Checkbox
              id="followUp"
              checked={followUpRequired}
              onCheckedChange={(checked) => setFollowUpRequired(checked as boolean)}
            />
            <div className="flex-1 space-y-2">
              <Label htmlFor="followUp">Follow-up required</Label>
              {followUpRequired && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="followUpDate">Follow-up Date</Label>
                    <Input
                      id="followUpDate"
                      type="date"
                      value={followUpDate}
                      onChange={(e) => setFollowUpDate(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="followUpPerson">Assigned To</Label>
                    <Input
                      id="followUpPerson"
                      value={followUpPerson}
                      onChange={(e) => setFollowUpPerson(e.target.value)}
                      placeholder="Name"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="flex items-start gap-3">
            <Checkbox
              id="clinicalReview"
              checked={clinicalReviewRequired}
              onCheckedChange={(checked) => setClinicalReviewRequired(checked as boolean)}
            />
            <div className="flex-1 space-y-2">
              <Label htmlFor="clinicalReview">Clinical review required</Label>
              {clinicalReviewRequired && (
                <Input
                  value={clinicalReviewer}
                  onChange={(e) => setClinicalReviewer(e.target.value)}
                  placeholder="Clinical lead name"
                />
              )}
            </div>
          </div>

          <div className="flex items-start gap-3">
            <Checkbox
              id="legalEscalation"
              checked={legalEscalation}
              onCheckedChange={(checked) => setLegalEscalation(checked as boolean)}
            />
            <div className="flex-1 space-y-2">
              <Label htmlFor="legalEscalation">Legal escalation required</Label>
              {legalEscalation && (
                <Textarea
                  value={legalNotes}
                  onChange={(e) => setLegalNotes(e.target.value)}
                  placeholder="Legal concerns and actions needed..."
                  rows={3}
                />
              )}
            </div>
          </div>

          {issueType === 'privacy_concern' && (
            <>
              <div className="flex items-center gap-3">
                <Checkbox
                  id="icoNotification"
                  checked={icoNotificationRequired}
                  onCheckedChange={(checked) => setIcoNotificationRequired(checked as boolean)}
                />
                <Label htmlFor="icoNotification" className="font-semibold text-red-500">
                  ICO Notification Required (within 72 hours of breach discovery)
                </Label>
              </div>

              <div className="flex items-center gap-3">
                <Checkbox
                  id="userNotification"
                  checked={userNotificationRequired}
                  onCheckedChange={(checked) => setUserNotificationRequired(checked as boolean)}
                />
                <Label htmlFor="userNotification" className="font-semibold text-red-500">
                  User Notification Required (GDPR Article 34 - high risk breach)
                </Label>
              </div>
            </>
          )}
        </div>
      </Card>

      {/* Resolution Summary */}
      <Card className="p-4">
        <h3 className="font-semibold mb-4">Resolution Summary *</h3>
        <Textarea
          value={resolutionSummary}
          onChange={(e) => setResolutionSummary(e.target.value)}
          placeholder="Brief summary of the investigation outcome and resolution (1-2 paragraphs). This will be visible to the user if they request it."
          rows={5}
        />
      </Card>

      {/* Warning if incomplete */}
      {!canComplete && (
        <Card className="p-4 bg-orange-500/10 border-orange-500/20">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-orange-500 mt-0.5" />
            <div className="flex-1">
              <p className="font-semibold text-orange-500 mb-1">Investigation Incomplete</p>
              <p className="text-sm text-muted-foreground">
                You must complete all required checklist items and fill in mandatory fields before closing this investigation.
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* Actions */}
      <div className="flex justify-end gap-3">
        <Button variant="outline" onClick={onCancel}>
          Save Draft & Exit
        </Button>
        <Button onClick={handleComplete} disabled={!canComplete}>
          <CheckCircle2 className="h-4 w-4 mr-2" />
          Complete Investigation
        </Button>
      </div>
    </div>
  );
}
